import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import { compareDecimalStrings, subtractDecimals } from "@/shared/utils/decimal";
import { type PaymentTaxLine, splitFeeTotal } from "@/shared/utils/paymentTaxes";
import type { PixKeyTypeEnum } from "@/shared/utils/pixKey";

import {
  CURRENCY_BY_RUNTIME_CORRIDOR,
  RAILS_BY_RUNTIME_CORRIDOR,
  type RuntimeCorridor,
} from "../constants/dispersions.constants";
import type { DispersionSuccessData } from "../hooks/useSubmitDispersion";
import {
  type BrebKeyType,
  buildArgentinaCbuCvuPayoutPayload,
  buildBrazilPixPayoutPayload,
  buildColombiaBrebPayoutPayload,
  buildColombiaTransfiyaPayoutPayload,
  buildMexicoSpeiPayoutPayload,
  type CbuCvuAccountIdType,
  type ColombianDocumentType,
  type CreatePayoutPayload,
  type PayoutStatus,
  type TransfiyaAccountType,
} from "../services/paymentsPayout.service";
import { type NativeQuote, type NativeQuoteRequest, quoteFeeView } from "../services/paymentsQuotes.service";
import type { DispersionFormData } from "../types/dispersions-api.types";
import { resolveRecipientDisplay } from "./recipientDisplay";

/** Every rail the send panel can pay through. */
export type SendRail = "BREB" | "TRANSFIYA" | "PIX" | "SPEI" | "CBU_CVU";

/**
 * Who carries the shipping cost. `sender`: the typed amount is what arrives and the cost is added to the
 * debit. `recipient`: the typed amount is the debit and the cost comes out of what arrives.
 */
export type SendFeeMode = "sender" | "recipient";

/**
 * What a send moves: from the wallet of `originCurrency` to someone in `country`, paid in that corridor's
 * `currency` over `rail`. When the two currencies differ the payout converts inside itself, at the rate the
 * quote froze.
 */
export interface SendRoute {
  originCurrency: string;
  country: RuntimeCorridor;
  currency: string;
  rail: SendRail;
}

export function sendRouteFor(originCurrency: string, country: RuntimeCorridor, rail: SendRail): SendRoute {
  return { originCurrency, country, currency: CURRENCY_BY_RUNTIME_CORRIDOR[country], rail };
}

/** The rails a corridor offers, in the order the panel shows them (the first is the default). */
export const railsFor = (country: RuntimeCorridor): readonly SendRail[] =>
  RAILS_BY_RUNTIME_CORRIDOR[country] as readonly SendRail[];

export const isCrossCurrencySend = (route: SendRoute): boolean => route.originCurrency !== route.currency;

/** The currency the amount is typed in: what arrives when the sender covers the cost, what leaves otherwise. */
export const amountCurrencyFor = (route: SendRoute, feeMode: SendFeeMode): string =>
  feeMode === "sender" ? route.currency : route.originCurrency;

export interface BrebSendRecipient {
  rail: "BREB";
  keyType: BrebKeyType;
  keyValue: string;
  /** The directory's masked holder — shown, never sent. */
  holderName: string | null;
  bankName: string | null;
  /** A scanned QR, verbatim: the payout pays its own target and forwards its reference. */
  qr?: { payload: string; reference: string | null };
}

export interface TransfiyaSendRecipient {
  rail: "TRANSFIYA";
  bankCode: string;
  bankName: string;
  accountType: TransfiyaAccountType;
  accountNumber: string;
  documentType: ColombianDocumentType;
  documentNumber: string;
  beneficiaryName: string;
}

export interface PixSendRecipient {
  rail: "PIX";
  keyType: PixKeyTypeEnum;
  /** As typed (masked); normalized by the payout builder. */
  keyValue: string;
  beneficiaryName: string;
}

export interface SpeiSendRecipient {
  rail: "SPEI";
  clabe: string;
  beneficiaryName: string;
  rfc: string | null;
}

export interface CbuCvuSendRecipient {
  rail: "CBU_CVU";
  accountType: CbuCvuAccountIdType;
  accountNumber: string;
  cuitCuil: string | null;
  beneficiaryName: string;
}

export type SendRecipient =
  | BrebSendRecipient
  | TransfiyaSendRecipient
  | PixSendRecipient
  | SpeiSendRecipient
  | CbuCvuSendRecipient;

/** The fee mode is expressed only by which amount is fixed — the quote DTO has no field for it. */
export function sendQuoteRequest(input: {
  route: SendRoute;
  amount: string;
  feeMode: SendFeeMode;
  organizationId?: string;
}): NativeQuoteRequest {
  return {
    origin_currency: input.route.originCurrency,
    destination_currency: input.route.currency,
    service: "payout",
    country: input.route.country,
    rail: input.route.rail,
    ...(input.feeMode === "sender" ? { receive_amount: input.amount } : { send_amount: input.amount }),
    ...(input.organizationId ? { organization_id: input.organizationId } : {}),
  };
}

export interface SendBreakdown {
  /** Leaves the wallet, in the origin currency. */
  debit: string;
  /**
   * The shipping cost in the delivery currency, taxes apart. On a same-currency send it is everything between
   * the debit and what arrives, so a conversion fee is never left unshown.
   */
  cost: string;
  /** Client-borne taxes, each on its own line, in the delivery currency. */
  taxes: PaymentTaxLine[];
  /** Reaches the person, in the delivery currency. */
  arrives: string;
  /** The conversion fee, in the origin currency. Null when nothing converts. */
  conversion: string | null;
}

export function sendBreakdown(quote: NativeQuote): SendBreakdown {
  const arrives = quote.amounts.payout_net ?? quote.amounts.receive;
  if (quote.currency.from === quote.currency.to) {
    const { commission, taxes } = splitFeeTotal(
      subtractDecimals(quote.amounts.send, arrives),
      quote.fees.payout?.taxes,
    );
    return { debit: quote.amounts.send, arrives, cost: commission, taxes, conversion: null };
  }
  // Two fees in two currencies: never summed, each shown in its own.
  const view = quoteFeeView(quote);
  return {
    debit: quote.amounts.send,
    arrives,
    cost: view.payout?.total ?? "0",
    taxes: view.taxes,
    conversion: view.conversion?.total ?? "0",
  };
}

/** Anything between the debit and what arrives, in either currency. */
export function sendHasCost(breakdown: SendBreakdown): boolean {
  if (breakdown.conversion === null) {
    return compareDecimalStrings(subtractDecimals(breakdown.debit, breakdown.arrives), "0") > 0;
  }
  return (
    compareDecimalStrings(breakdown.cost, "0") > 0 ||
    compareDecimalStrings(breakdown.conversion, "0") > 0 ||
    breakdown.taxes.length > 0
  );
}

/**
 * The exchange rate written the way a person reads it: one unit of the stronger currency in the weaker one
 * ("1 BRL = 742,10 COP" rather than "1 COP = 0,0013 BRL"). Display only — the quote keeps the exact rate, and
 * no amount is ever derived from this figure.
 */
export function readableRate(quote: NativeQuote): { base: string; quote: string; value: number } | null {
  const rate = Number(quote.rate.effective);
  if (!Number.isFinite(rate) || rate <= 0 || quote.currency.from === quote.currency.to) return null;
  return rate >= 1
    ? { base: quote.currency.from, quote: quote.currency.to, value: rate }
    : { base: quote.currency.to, quote: quote.currency.from, value: 1 / rate };
}

/** A readable rate as one line, `1 BRL = 742,10 COP`, with more decimals for a rate close to 1. */
export function formatRateLine(rate: { base: string; quote: string; value: number }, locale: string): string {
  const value = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: rate.value >= 10 ? 2 : 4,
  }).format(rate.value);
  return `1 ${rate.base} = ${value} ${rate.quote}`;
}

/** `readableRate` as one line. */
export function formatReadableRate(quote: NativeQuote, locale: string): string | null {
  const rate = readableRate(quote);
  return rate ? formatRateLine(rate, locale) : null;
}

/** Whether a quote still prices the send on screen; a stale one must never be redeemed. */
export function quoteMatchesSend(quote: NativeQuote, route: SendRoute): boolean {
  return (
    quote.service === "payout" &&
    quote.currency.from === route.originCurrency &&
    quote.currency.to === route.currency &&
    quote.payout?.country === route.country &&
    quote.payout?.rail === route.rail
  );
}

/**
 * The payer kind the corridor limits are checked against, read off the destination exactly as the backend
 * does (`payoutPayerTypeFor`): a NIT, a CNPJ key or a 12-character RFC is a company.
 */
export function sendPayerType(recipient: SendRecipient | null): LimitPayerType | null {
  if (!recipient) return null;
  switch (recipient.rail) {
    case "TRANSFIYA":
      return recipient.documentType === "NIT" ? "COMPANY" : "PERSON";
    case "PIX":
      return recipient.keyType === "CNPJ" ? "COMPANY" : "PERSON";
    case "SPEI":
      return (recipient.rfc ?? "").trim().length === 12 ? "COMPANY" : "PERSON";
    default:
      return "PERSON";
  }
}

export function buildSendPayload(input: {
  organizationId: string;
  quote: NativeQuote;
  recipient: SendRecipient;
  concept: string;
  idempotencyKey: string;
}): CreatePayoutPayload {
  const concept = input.concept.trim() || undefined;
  const common = {
    organizationId: input.organizationId,
    receiveAmount: input.quote.amounts.receive,
    quoteId: input.quote.id,
    concept,
    idempotencyKey: input.idempotencyKey,
  };
  const { recipient } = input;
  switch (recipient.rail) {
    case "TRANSFIYA":
      return buildColombiaTransfiyaPayoutPayload({
        ...common,
        accountType: recipient.accountType,
        accountNumber: recipient.accountNumber,
        bankCode: recipient.bankCode,
        documentType: recipient.documentType,
        documentNumber: recipient.documentNumber,
        beneficiaryName: recipient.beneficiaryName,
      });
    case "PIX":
      return buildBrazilPixPayoutPayload({
        ...common,
        pixKeyType: recipient.keyType,
        pixKeyValue: recipient.keyValue,
        beneficiaryName: recipient.beneficiaryName.trim() || undefined,
      });
    case "SPEI":
      return buildMexicoSpeiPayoutPayload({
        ...common,
        clabe: recipient.clabe,
        beneficiaryName: recipient.beneficiaryName,
        rfc: recipient.rfc ?? undefined,
      });
    case "CBU_CVU":
      return buildArgentinaCbuCvuPayoutPayload({
        ...common,
        accountIdType: recipient.accountType,
        accountIdValue: recipient.accountNumber,
        cuitCuil: recipient.cuitCuil ?? undefined,
        beneficiaryName: recipient.beneficiaryName.trim() || undefined,
      });
    case "BREB":
      return buildColombiaBrebPayoutPayload({
        ...common,
        brebKeyType: recipient.keyType,
        brebKeyValue: recipient.keyValue,
        brebQrString: recipient.qr?.payload,
      });
  }
}

/** The wizard's per-corridor field names, which the receipt and "save this recipient" read. */
function recipientFieldsOf(recipient: SendRecipient, concept: string): Record<string, unknown> {
  const description = concept.trim();
  switch (recipient.rail) {
    case "TRANSFIYA":
      return {
        col_rail: "TRANSFIYA",
        transfiya_bank_code: recipient.bankCode,
        transfiya_account_type: recipient.accountType,
        transfiya_account_number: recipient.accountNumber,
        transfiya_document_type: recipient.documentType,
        transfiya_document_number: recipient.documentNumber,
        beneficiary: recipient.beneficiaryName,
        description,
      };
    case "PIX":
      return {
        pix_type: recipient.keyType,
        pix_key: recipient.keyValue,
        beneficiary: recipient.beneficiaryName,
        description,
      };
    case "SPEI":
      return {
        clabe: recipient.clabe,
        beneficiary: recipient.beneficiaryName,
        rfc: recipient.rfc ?? "",
        description,
      };
    case "CBU_CVU":
      return {
        account_type: recipient.accountType,
        account_number: recipient.accountNumber,
        cuit: recipient.cuitCuil ?? "",
        beneficiary: recipient.beneficiaryName,
        description,
      };
    case "BREB":
      return {
        col_rail: "BREB",
        breb_key_type: recipient.keyType,
        breb_key_value: recipient.keyValue,
        description,
      };
  }
}

function recipientDisplayOf(recipient: SendRecipient): string {
  return recipient.rail === "BREB"
    ? resolveRecipientDisplay({
        resolvedHolder: recipient.holderName,
        brebKeyType: recipient.keyType,
        brebKeyValue: recipient.keyValue,
      })
    : resolveRecipientDisplay({ typedName: recipient.beneficiaryName });
}

/** The receipt reads the wizard's snapshot shape, so the send is described in it. */
export function sendSuccessData(input: {
  payout: { id: string; status: PayoutStatus; created_at: string };
  quote: NativeQuote;
  route: SendRoute;
  recipient: SendRecipient;
  concept: string;
  feeMode: SendFeeMode;
}): DispersionSuccessData {
  const { recipient, quote, route } = input;
  const formData: DispersionFormData = {
    origin_currency: route.originCurrency,
    destination: route.country,
    amount_origin: quote.amounts.send,
    amount_destination: quote.amounts.payout_net ?? quote.amounts.receive,
    recipientFields: recipientFieldsOf(recipient, input.concept),
    recipientMode: "manual",
    selectedClientId: null,
    selectedClientAccountId: null,
    selectedClientAccountMethod: null,
  };
  return {
    dispersionResponse: {
      id: input.payout.id,
      status: input.payout.status,
      createdAt: input.payout.created_at,
    },
    formData,
    quoteData: quote,
    recipientDisplay: recipientDisplayOf(recipient),
    pricedFrom: input.feeMode === "sender" ? "destination" : "origin",
  };
}
